import { useState } from 'react';
import { keptFor } from '../recorder/videoRetention';
import { Play, Video, Volume2, X } from 'lucide-react';
import type { StageMedia } from '../annotations/AnnotationCanvas';
import styles from './WalkthroughClip.module.css';

/**
 * The moment of the walkthrough this ticket came from (TOADO-366).
 *
 * The clips were pure cost before this: roughly 104MB per walkthrough, kept
 * for as long as the tickets are, and no human could watch a second of it.
 * Showing them is what turns the most expensive thing in the product into the
 * most differentiated - a developer sees the pointer move, the hover, the
 * click, and the moment the reporter decided something was wrong. A
 * screenshot tool cannot answer "what were you actually doing".
 *
 * The whole backend was already there: `capture.clipUrl` is on the ticket,
 * and `/tickets/:id/recording-assets/clip` streams it under the session
 * cookie. Nothing rendered it.
 *
 * It sits in the image row as a tile of its own, just after the screenshot
 * (Jason, 10 Oct, board ticket t-cnb0zy): the screenshot with a play mark,
 * and "Watch this moment" over "See what the reporter was doing" beside it,
 * two short lines so the row keeps room for more images. Choosing it plays
 * the clip where the image is. The first version sat under the screenshot
 * instead, because swapping the two meant unmounting or hiding the annotation
 * canvas and risking a drawing somebody was part-way through; the clip is
 * laid OVER the canvas, which stays mounted underneath, so that risk does not
 * come back.
 */
export interface WalkthroughClipSource {
  clipUrl: string;
  /** The still the pin was placed on - the correct frame for this clip. */
  posterUrl?: string;
  /**
   * What the walkthrough recorded (TOADO-380).
   *
   * An audio walkthrough's clip is narration with no picture, and a `<video>`
   * element pointed at one renders a black rectangle that plays. So an audio
   * clip plays in a bar over the foot of the image, which stays in view, and
   * only a video takes the image's place.
   */
  kind?: 'video' | 'audio';
}

/**
 * The clip as the annotation canvas shows it: a tile for its image row and,
 * once chosen, a player over the image. Undefined when the ticket has no clip
 * to play, an expired one included (its notice is `WalkthroughClipNotice`).
 */
export function useWalkthroughClip(source: WalkthroughClipSource | null): StageMedia | undefined {
  /**
   * Nothing is fetched until somebody asks.
   *
   * A clip is 1.5MB to 29MB. Mounting a `<video src>` on every walkthrough
   * ticket would pull that down because a page was opened, which is a
   * bandwidth bill for video nobody chose to watch. The player is not mounted
   * until this flips, and the tile shows the screenshot the page already has,
   * so the guarantee is a complete one: no element, no request.
   *
   * Once it does flip the element preloads eagerly. `preload="none"` looked
   * like extra thrift and was a second click: the person had already said
   * watch this, and the browser answered by fetching nothing and waiting to
   * be asked again.
   */
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  /**
   * Which clip the two flags above are about.
   *
   * This hook lives in the ticket page, which `TicketPage` renders at the same
   * place for every ticket, so nothing in the routing makes React remount it
   * when the route parameter changes. Today it remounts anyway, because the
   * tree above unmounts while the next ticket loads; that is an accident of how
   * the page above renders, not a property anything states, and the browser
   * spec that moves between walkthroughs passes without this reset for that
   * reason. What losing both would cost is specific: the next walkthrough
   * downloading unasked, or a clip that failed to decode hiding the next
   * ticket's working one behind "could not be loaded". The unit test holds
   * this directly.
   *
   * Adjusting state during render is React's own answer to a prop change
   * invalidating state, and is preferred to an effect: it happens before
   * anything paints, so no frame shows the wrong clip's state.
   */
  const clipUrl = source?.clipUrl ?? '';
  const [shownClipUrl, setShownClipUrl] = useState(clipUrl);
  if (clipUrl !== shownClipUrl) {
    setShownClipUrl(clipUrl);
    setOpen(false);
    setFailed(false);
  }

  if (!source || !source.clipUrl) return undefined;
  const kind = source.kind ?? 'video';
  return {
    tile: (
      <WalkthroughClipTile
        kind={kind}
        {...(source.posterUrl ? { posterUrl: source.posterUrl } : {})}
        active={open}
        onOpen={() => setOpen(true)}
      />
    ),
    cover: open
      ? {
        hidesImage: kind === 'video',
        node: (
          <WalkthroughClipPlayer
            clipUrl={source.clipUrl}
            {...(source.posterUrl ? { posterUrl: source.posterUrl } : {})}
            kind={kind}
            failed={failed}
            onFailed={() => setFailed(true)}
            onClose={() => setOpen(false)}
          />
        ),
      }
      : null,
    onShowImage: () => setOpen(false),
  };
}

function WalkthroughClipTile({ kind, posterUrl, active, onOpen }: {
  kind: 'video' | 'audio';
  posterUrl?: string;
  active: boolean;
  onOpen: () => void;
}) {
  const audio = kind === 'audio';
  return (
    <button
      type="button"
      className={styles.tile}
      data-active={active || undefined}
      data-testid="walkthrough-clip-tile"
      onClick={onOpen}
    >
      <span className={styles.tileThumb}>
        {/* The screenshot the page already has, not a frame of the clip: no video bytes until asked. */}
        {posterUrl && <img className={styles.tilePoster} src={posterUrl} alt="" />}
        <span className={styles.tilePlay} aria-hidden data-testid="walkthrough-clip-play">
          {audio ? <Volume2 size={12} /> : <Play size={11} fill="currentColor" />}
        </span>
      </span>
      <span className={styles.tileText}>
        <span className={styles.tileLabel}>{audio ? 'Hear this moment' : 'Watch this moment'}</span>
        <span className={styles.tileHint}>
          {audio ? 'Hear what the reporter said' : 'See what the reporter was doing'}
        </span>
      </span>
    </button>
  );
}

function WalkthroughClipPlayer({ clipUrl, posterUrl, kind, failed, onFailed, onClose }: {
  clipUrl: string;
  posterUrl?: string;
  kind: 'video' | 'audio';
  failed: boolean;
  onFailed: () => void;
  onClose: () => void;
}) {
  const audio = kind === 'audio';
  const shell = audio ? styles.audioBar : styles.videoStage;
  // Choosing the screenshot's thumbnail does the same; this is the way back
  // from where the person is looking.
  const close = audio ? (
    <button type="button" className={styles.closeIcon} onClick={onClose} aria-label="Close the narration">
      <X size={14} aria-hidden />
    </button>
  ) : (
    <button type="button" className={styles.close} onClick={onClose}>
      <X size={14} aria-hidden />
      Show the screenshot
    </button>
  );

  if (failed) {
    return (
      <div className={shell} data-testid="walkthrough-clip-player">
        <p className={styles.gone}>
          <Video size={14} aria-hidden />
          This walkthrough clip could not be loaded.
        </p>
        {close}
      </div>
    );
  }

  return (
    <div className={shell} data-testid="walkthrough-clip-player">
      {audio ? (
        <audio
          className={styles.audio}
          src={clipUrl}
          controls
          autoPlay
          preload="auto"
          onError={onFailed}
          aria-label="Walkthrough narration for this ticket"
        />
      ) : (
        <video
          className={styles.video}
          src={clipUrl}
          {...(posterUrl ? { poster: posterUrl } : {})}
          controls
          autoPlay
          // Best effort: browsers refuse autoplay with sound often enough that
          // this cannot be the only way in, which is why `controls` is not
          // optional and the element loads whether or not playback starts.
          playsInline
          preload="auto"
          onError={onFailed}
          // Named for a screen reader, which otherwise announces only "video".
          aria-label="Walkthrough clip for this ticket"
        />
      )}
      {close}
    </div>
  );
}

/**
 * What the image row says when the clip is gone (TOADO-367).
 *
 * Two audiences and two sentences. A paid workspace has lost video that aged
 * out of a 45-day window and wants to know that is what happened, so nobody
 * hunts for a bug. A free workspace has lost video after a week, and the only
 * useful thing to say is how to keep it - the clip is the differentiated part
 * of this product and the moment somebody misses one is the moment the case
 * for paying makes itself.
 *
 * It sits in the carousel's right slot, beside the images, rather than under
 * the screenshot where the player lives. There is no player to sit under.
 */
/**
 * A walkthrough moment filed as a ticket during the recording, whose video is
 * still being cut (TOADO-900). The ticket is told when it lands and the
 * player takes this place.
 */
export function WalkthroughClipProcessing() {
  return (
    <span
      data-testid="walkthrough-clip-processing"
      title="The video of this moment is still being prepared. It appears here when it is ready."
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, height: 28, padding: '0 10px',
        borderRadius: 'var(--r-full)', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap',
        background: 'var(--bg-subtle)', color: 'var(--text-secondary)', border: '1px solid var(--border)',
      }}
    >
      <Video size={12} aria-hidden />
      Video processing
    </span>
  );
}

export function WalkthroughClipNotice({ plan, retentionDays, onUpgrade }: {
  /**
   * `unknown` is its own case and must stay one. Billing can be unresolved -
   * the request failed, or the ticket's workspace is still loading - and
   * reading that as `free` tells a paying workspace to upgrade. It gets the
   * plain notice, and says nothing about how long video is kept, because it
   * does not know which window applies.
   */
  plan: 'free' | 'paid' | 'unknown';
  /** How long the ticket's workspace keeps video (`useVideoRetentionDays`); unknown says no number. */
  retentionDays?: number;
  /** Opens billing. Absent when the viewer cannot act on it. */
  onUpgrade?: () => void;
}) {
  const free = plan === 'free';
  const label = free
    ? 'Get Personal or Team to keep clips'
    : 'Walkthrough video expired';
  const keptForGood = 'The ticket, its description and what was said are kept for good.';
  const days = keptFor(retentionDays, '');
  const title = free
    ? `Free workspaces keep walkthrough video ${days || 'for a short time'}. ${keptForGood}`
    : plan === 'paid' && days
      ? `Walkthrough video is kept ${days}. ${keptForGood}`
      : keptForGood;

  const body = (
    <>
      <Video size={12} aria-hidden />
      {label}
    </>
  );
  const shared: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 28,
    padding: '0 10px',
    borderRadius: 'var(--r-full)',
    fontSize: 12,
    fontWeight: 500,
    whiteSpace: 'nowrap',
  };

  if (!free || !onUpgrade) {
    return (
      <span
        title={title}
        data-testid="walkthrough-clip-notice"
        data-plan={plan}
        style={{
          ...shared,
          background: 'var(--bg-subtle)',
          color: 'var(--text-secondary)',
          border: '1px solid var(--border)',
        }}
      >
        {body}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onUpgrade}
      title={title}
      data-testid="walkthrough-clip-notice"
      data-plan="free"
      style={{
        ...shared,
        cursor: 'pointer',
        background: 'var(--accent-bg, var(--bg-subtle))',
        color: 'var(--accent)',
        border: '1px solid var(--accent)',
      }}
    >
      {body}
    </button>
  );
}
